import React, { useState } from 'react';
import { MapPin, Plus, Edit2, Trash2, Building2, Check } from 'lucide-react';
import { useLocations, Location } from '../../contexts/LocationContext';

export function LocationManager() {
  const { locations, addLocation, updateLocation, deleteLocation } = useLocations();
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    type: 'branch' as Location['type'],
    providerNumbers: '',
  });

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      type: 'branch',
      providerNumbers: '',
    });
  };

  const handleAdd = () => {
    if (!formData.name.trim()) return;
    
    addLocation({
      name: formData.name.trim(),
      address: formData.address.trim(),
      type: formData.type,
      isActive: true,
      providerNumbers: formData.providerNumbers
        .split(',')
        .map(p => p.trim())
        .filter(p => p),
    });
    
    resetForm();
    setIsAdding(false);
  };

  const handleEdit = (location: Location) => {
    setEditingId(location.id);
    setFormData({
      name: location.name,
      address: location.address,
      type: location.type,
      providerNumbers: location.providerNumbers.join(', '),
    });
  };

  const handleSaveEdit = () => {
    if (!editingId || !formData.name.trim()) return;
    
    updateLocation(editingId, {
      name: formData.name.trim(),
      address: formData.address.trim(),
      type: formData.type,
      providerNumbers: formData.providerNumbers
        .split(',')
        .map(p => p.trim())
        .filter(p => p),
    });
    
    resetForm();
    setEditingId(null);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this location?')) {
      deleteLocation(id);
    }
  };

  const getTypeColor = (type: Location['type']) => {
    switch (type) {
      case 'main': return 'bg-blue-500/20 text-blue-400';
      case 'branch': return 'bg-green-500/20 text-green-400';
      case 'satellite': return 'bg-purple-500/20 text-purple-400';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-blue-400" />
          <h3 className="text-lg font-medium text-slate-200">Multi-location Management</h3>
        </div>
        {!isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 
                     text-white rounded-lg transition-colors text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            Add Location
          </button>
        )}
      </div>

      {/* Add Location Form */}
      {isAdding && (
        <div className="bg-slate-800/50 rounded-lg p-4 border border-blue-500/30">
          <h4 className="text-sm font-medium text-slate-200 mb-4">Add New Location</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Location Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Baltimore Office"
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg 
                         text-slate-200 placeholder-slate-500 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Type</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as Location['type'] })}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg 
                         text-slate-200 text-sm focus:outline-none focus:border-blue-500"
              >
                <option value="main">Main Office</option>
                <option value="branch">Branch Office</option>
                <option value="satellite">Satellite Location</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-slate-400 mb-1">Address</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Main St, Baltimore, MD 21201"
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg 
                         text-slate-200 placeholder-slate-500 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-slate-400 mb-1">Provider Numbers (comma-separated)</label>
              <input
                type="text"
                value={formData.providerNumbers}
                onChange={(e) => setFormData({ ...formData, providerNumbers: e.target.value })}
                placeholder="e.g., 1234567, 7654321"
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg 
                         text-slate-200 placeholder-slate-500 text-sm focus:outline-none focus:border-blue-500"
              />
              <p className="text-xs text-slate-500 mt-1">
                Services from these provider numbers will be associated with this location
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={() => { resetForm(); setIsAdding(false); }}
              className="px-3 py-1.5 text-slate-400 hover:text-slate-200 transition-colors text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              disabled={!formData.name.trim()}
              className="flex items-center gap-1 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 
                       disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg 
                       transition-colors text-sm font-medium"
            >
              <Check className="w-4 h-4" />
              Add Location
            </button>
          </div>
        </div>
      )}

      {/* Locations List */}
      {locations.length === 0 ? (
        <div className="bg-slate-800/50 rounded-lg p-8 text-center">
          <MapPin className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 mb-1">No locations configured</p>
          <p className="text-slate-500 text-sm">
            Add locations to filter and organize your data by facility
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {locations.map((location) => (
            <div
              key={location.id}
              className={`bg-slate-800/50 rounded-lg p-4 border ${
                editingId === location.id ? 'border-blue-500/50' : 'border-slate-700'
              }`}
            >
              {editingId === location.id ? (
                // Edit Mode
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Location Name *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg 
                                 text-slate-200 text-sm focus:outline-none focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Type</label>
                      <select
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value as Location['type'] })}
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg 
                                 text-slate-200 text-sm focus:outline-none focus:border-blue-500"
                      >
                        <option value="main">Main Office</option>
                        <option value="branch">Branch Office</option>
                        <option value="satellite">Satellite Location</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs text-slate-400 mb-1">Address</label>
                      <input
                        type="text"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg 
                                 text-slate-200 text-sm focus:outline-none focus:border-blue-500"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs text-slate-400 mb-1">Provider Numbers</label>
                      <input
                        type="text"
                        value={formData.providerNumbers}
                        onChange={(e) => setFormData({ ...formData, providerNumbers: e.target.value })}
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg 
                                 text-slate-200 text-sm focus:outline-none focus:border-blue-500"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => { resetForm(); setEditingId(null); }}
                      className="px-3 py-1.5 text-slate-400 hover:text-slate-200 transition-colors text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveEdit}
                      className="flex items-center gap-1 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 
                               text-white rounded-lg transition-colors text-sm font-medium"
                    >
                      <Check className="w-4 h-4" />
                      Save Changes
                    </button>
                  </div>
                </div>
              ) : (
                // View Mode
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-slate-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-slate-200">{location.name}</h4>
                        <span className={`px-2 py-0.5 rounded text-xs ${getTypeColor(location.type)}`}>
                          {location.type}
                        </span>
                      </div>
                      {location.address && (
                        <p className="text-sm text-slate-400 mt-1">{location.address}</p>
                      )}
                      {location.providerNumbers.length > 0 && (
                        <p className="text-xs text-slate-500 mt-1">
                          Providers: {location.providerNumbers.join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleEdit(location)}
                      className="p-1.5 text-slate-400 hover:text-slate-200 transition-colors"
                      title="Edit location"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(location.id)}
                      className="p-1.5 text-slate-400 hover:text-red-400 transition-colors"
                      title="Delete location"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Info */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
        <p className="text-sm text-slate-300">
          <strong className="text-blue-400">How it works:</strong> Associate provider numbers with each location. 
          When you upload a report, services will be automatically grouped by location based on the provider number. 
          Use the location selector in the header to filter your dashboard view.
        </p>
      </div>
    </div>
  );
}
